import DOMPurify from 'dompurify';

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The user input to sanitize
 * @param {Object} options - Optional configuration for sanitization
 * @returns {string} - The sanitized input
 */
export const sanitizeInput = (input, options = {}) => {
  // Return empty string for null/undefined
  if (input == null) {
    return '';
  }

  // Convert to string if not already
  const inputStr = String(input);

  // Default configuration: strip all HTML tags and attributes
  const defaultConfig = {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true, // Keep the text content, just remove tags
    ...options
  };

  return DOMPurify.sanitize(inputStr, defaultConfig);
};

/**
 * Validates and sanitizes text input with maximum length check
 * @param {string} input - The user input to validate and sanitize
 * @param {number} maxLength - Maximum allowed length (default: 10000)
 * @returns {string} - The validated and sanitized input
 */
export const validateAndSanitizeText = (input, maxLength = 10000) => {
  if (!input) {
    return '';
  }

  const sanitized = sanitizeInput(input);

  // Truncate if exceeds maximum length
  if (sanitized.length > maxLength) {
    return sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Sanitizes an object's string properties
 * @param {Object} obj - The object containing properties to sanitize
 * @param {Array<string>} fields - Array of field names to sanitize
 * @param {number} maxLength - Maximum allowed length for each field
 * @returns {Object} - Object with sanitized properties
 */
export const sanitizeObject = (obj, fields, maxLength = 10000) => {
  const sanitized = { ...obj };

  fields.forEach(field => {
    if (sanitized[field] !== undefined && sanitized[field] !== null) {
      sanitized[field] = validateAndSanitizeText(sanitized[field], maxLength);
    }
  });

  return sanitized;
};

/**
 * Sanitizes an array of strings
 * @param {Array<string>} arr - Array of strings to sanitize
 * @param {number} maxLength - Maximum allowed length for each item
 * @returns {Array<string>} - Array with sanitized strings
 */
export const sanitizeArray = (arr, maxLength = 1000) => {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr.map(item => validateAndSanitizeText(item, maxLength));
};
